import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';

const markdownSx = {
  '& p': { mt: 0, mb: 1.5 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { mt: 0, mb: 1.5, pl: 2.5 },
  '& li': { mb: 0.5 },
  '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, fontWeight: 600 },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.25rem' },
  '& h3': { fontSize: '1.1rem' },
  '& pre': {
    overflow: 'auto',
    p: 1.5,
    borderRadius: 1,
    bgcolor: 'action.hover',
    fontSize: '0.875rem',
    mb: 1.5,
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.875em',
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: 0.5,
  },
  '& pre code': { bgcolor: 'transparent', p: 0 },
  '& blockquote': {
    m: 0,
    mb: 1.5,
    pl: 2,
    borderLeft: 3,
    borderColor: 'divider',
    color: 'text.secondary',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    mb: 1.5,
    fontSize: '0.875rem',
  },
  '& th, & td': {
    border: 1,
    borderColor: 'divider',
    px: 1,
    py: 0.5,
  },
  '& hr': { my: 2, border: 0, borderTop: 1, borderColor: 'divider' },
};

const markdownComponents = {
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
};

export default function ProductDescriptionContent({ description }) {
  const { translate } = useTranslation();
  const text = String(description || '').trim();

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  if (!text) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate('products.view_description_empty')}
      </Typography>
    );
  }

  return (
    <Box sx={markdownSx}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
        {text}
      </ReactMarkdown>
    </Box>
  );
}
